"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";

function TeamAvatar({ name, photo }: { name: string; photo: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="relative w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center overflow-hidden"
      style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)" }}
    >
      {!failed ? (
        <Image
          src={photo}
          alt={name}
          fill
          sizes="80px"
          className="object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span style={{ color: "#C9A84C", fontFamily: "'Cormorant', serif", fontSize: "1.5rem" }}>
          {name.charAt(0)}
        </span>
      )}
    </div>
  );
}

// To add a real photo: drop the file in public/team/ using the filename
// below, then it renders automatically (falls back to initials if missing).
const TEAM = [
  {
    name: "Pareen Kanakhara",
    role: "Founder & CEO",
    bio: "Built Aurelius & Co to solve a real problem he lived — Instagram DMs slipping through the cracks and leads going cold. Drives vision, product, and every client relationship.",
    linkedin: null,
    photo: "/team/pareen-kanakhara.jpeg",
  },
  {
    name: "Harsh Raithatha",
    role: "Developer",
    bio: "Engineered the full-stack platform — from Instagram Graph API integration and OAuth flows to the automation engine and real-time dashboard.",
    linkedin: "https://in.linkedin.com/in/raithathaharsh",
    photo: "/team/harsh-raithatha.jpeg",
  },
];

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen px-6 py-32" style={{ background: "#0A0908" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-16">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-3"
              style={{ color: "rgba(201,168,76,0.7)", fontFamily: "'Montserrat', sans-serif" }}
            >
              About Us
            </p>
            <h1
              className="text-4xl md:text-5xl font-light leading-tight mb-6"
              style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
            >
              Built for brands that{" "}
              <span style={{ color: "#C9A84C", fontStyle: "italic" }}>can't afford to miss</span>
            </h1>
            <p
              className="text-sm leading-relaxed mb-8"
              style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Aurelius &amp; Co was founded by Pareen Kanakhara after watching Instagram DMs turn into a graveyard of missed leads. Every unanswered message was a potential client choosing a competitor who responded first.
            </p>
            <p
              className="text-sm leading-relaxed mb-8"
              style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
            >
              We built what didn't exist — an Instagram automation platform that's 100% compliant with the official Graph API, feels personal to every recipient, and gives marketing teams their time back. No shortcuts, no risky workarounds, no bots that sound like bots.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 max-w-2xl mx-auto w-full">
            {TEAM.map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 text-center"
                style={{ border: "1px solid rgba(201,168,76,0.1)" }}
              >
                <TeamAvatar name={member.name} photo={member.photo} />
                {member.linkedin ? (
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-light mb-1 block"
                    style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif", textDecoration: "none", borderBottom: "1px solid rgba(201,168,76,0.3)", paddingBottom: "2px", display: "inline-block", transition: "color 0.2s, border-color 0.2s" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#C9A84C"; (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(201,168,76,0.7)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#F5F0E8"; (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(201,168,76,0.3)"; }}
                  >
                    {member.name}
                  </a>
                ) : (
                  <h3
                    className="text-lg font-light mb-1"
                    style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
                  >
                    {member.name}
                  </h3>
                )}
                <p
                  className="text-xs tracking-widest uppercase mb-3"
                  style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}
                >
                  {member.role}
                </p>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <h2
              className="text-2xl font-light mb-4"
              style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
            >
              Our Mission
            </h2>
            <p
              className="text-sm leading-relaxed max-w-2xl mx-auto"
              style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
            >
              To help businesses never miss a customer conversation on Instagram. We believe in automation that feels personal, tools that respect platform rules, and software that marketing teams actually enjoy using.
            </p>
          </div>
        </motion.div>
      </main>
      <Footer />
    </>
  );
}
